import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';
import { doc, DocumentData, runTransaction } from 'firebase/firestore';
import app, { db } from '../firebase';
import { FcmDevice } from '../types';

/** Firebase Console → Project settings → Cloud Messaging → Web Push certificates */
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

const DAY_MS = 24 * 60 * 60 * 1000;
/** A device silent this long has almost certainly rotated its token; stop paying to reach it. */
const STALE_DEVICE_DAYS = 60;
/** Upper bound so the array cannot grow without limit on shared browsers. */
const MAX_DEVICES = 10;
/** Refresh `lastSeenAt` at most this often, so a routine app open costs no write. */
const TOUCH_INTERVAL_MS = 12 * 60 * 60 * 1000;
/** Shown for a registration we cannot attribute to a device — legacy, or written malformed. */
const UNKNOWN_DEVICE_LABEL = 'Dispozitiv neidentificat';

let foregroundBound = false;

/**
 * Why a device did or did not end up registered. Both returned and logged, because every one of
 * these exits used to be silent: a rejected `getToken` or a missing VAPID key looked exactly
 * like "nothing happened", and the only way to tell them apart was reading build timestamps.
 */
export type PushSyncStatus =
  | 'registered'
  | 'no-user'
  | 'no-notification-api'
  | 'no-service-worker'
  | 'missing-vapid-key'
  | 'permission-not-granted'
  | 'unsupported-browser'
  | 'no-token-issued'
  | 'user-doc-missing'
  | 'subscribe-failed';

function report(status: PushSyncStatus, detail?: unknown): PushSyncStatus {
  if (status === 'registered') {
    console.info('[push] device registered');
  } else {
    console.warn('[push] not registered:', status, detail ?? '');
  }
  return status;
}

export const canUseWebPush = (): boolean =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  Boolean(VAPID_KEY);

/** First match wins: an Android user agent also says Linux. */
const PLATFORM_PATTERNS: [RegExp, string][] = [
  [/iPhone/, 'iPhone'],
  [/iPad/, 'iPad'],
  [/Android/, 'Android'],
  [/Macintosh/, 'Mac'],
  [/Windows/, 'Windows'],
  [/Linux/, 'Linux'],
];

/** First match wins: Edge and Opera also claim Chrome, and Chrome also claims Safari. */
const BROWSER_PATTERNS: [RegExp, string][] = [
  [/Edg\//, 'Edge'],
  [/OPR\//, 'Opera'],
  [/Chrome\//, 'Chrome'],
  [/Firefox\//, 'Firefox'],
  [/Safari\//, 'Safari'],
];

function matchUserAgent(ua: string, patterns: [RegExp, string][], fallback: string): string {
  return patterns.find(([pattern]) => pattern.test(ua))?.[1] ?? fallback;
}

/** Label for the Firestore record — enough to recognise the device, no fingerprinting. */
function describeDevice(): string {
  const ua = navigator.userAgent;
  const platform = matchUserAgent(ua, PLATFORM_PATTERNS, 'Dispozitiv');
  const browser = matchUserAgent(ua, BROWSER_PATTERNS, 'browser');

  const installed =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  return `${platform} · ${installed ? 'aplicație instalată' : browser}`;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  const candidate = value as { toDate?: () => Date } | null;
  if (candidate && typeof candidate.toDate === 'function') {
    const parsed = candidate.toDate();
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * Reads the labelled registrations, folding in any token that only exists in the legacy
 * flat list. Those get `now` as their first sighting, so a device registered by an older
 * build keeps working and has the full stale window to check in and identify itself.
 */
function readDevices(data: DocumentData | undefined, now: Date): FcmDevice[] {
  const devices: FcmDevice[] = [];
  const seen = new Set<string>();

  const labelled = Array.isArray(data?.fcmDevices) ? data.fcmDevices : [];
  for (const entry of labelled) {
    const token = typeof entry?.token === 'string' ? entry.token : '';
    if (token.length < 20 || seen.has(token)) continue;
    seen.add(token);
    devices.push({
      token,
      label: typeof entry?.label === 'string' && entry.label ? entry.label : UNKNOWN_DEVICE_LABEL,
      lastSeenAt: toDate(entry?.lastSeenAt) ?? now,
    });
  }

  const legacy = Array.isArray(data?.fcmTokens) ? data.fcmTokens : [];
  for (const token of legacy) {
    if (typeof token !== 'string' || token.length < 20 || seen.has(token)) continue;
    seen.add(token);
    devices.push({ token, label: UNKNOWN_DEVICE_LABEL, lastSeenAt: now });
  }

  return devices;
}

/**
 * Parses the stored registrations into the app-level shape. Exported because Firestore hands
 * back `Timestamp` for `lastSeenAt`, so nothing outside this module should read the raw field.
 */
export function fcmDevicesFromUserData(data: DocumentData | undefined): FcmDevice[] {
  return readDevices(data, new Date());
}

/**
 * Records this device against the user and drops registrations that went quiet.
 *
 * Transactional because the whole array is rewritten: a plain read-modify-write would drop a
 * sibling device that registered between the read and the write, or mirror back a token the
 * server pruned in the meantime and so resurrect a dead registration.
 */
async function upsertDevice(userId: string, token: string): Promise<boolean> {
  const ref = doc(db, 'users', userId);
  const label = describeDevice();
  let userDocFound = false;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    userDocFound = true;

    const now = new Date();
    const existing = readDevices(snap.data(), now);
    const own = existing.find((device) => device.token === token);

    const kept = existing.filter(
      (device) =>
        device.token === token ||
        now.getTime() - device.lastSeenAt.getTime() < STALE_DEVICE_DAYS * DAY_MS
    );

    const alreadyCurrent =
      own !== undefined &&
      own.label === label &&
      now.getTime() - own.lastSeenAt.getTime() < TOUCH_INTERVAL_MS;

    if (alreadyCurrent && kept.length === existing.length && existing.length <= MAX_DEVICES) {
      return;
    }

    const devices = [
      { token, label, lastSeenAt: now },
      ...kept.filter((device) => device.token !== token),
    ]
      .sort((a, b) => b.lastSeenAt.getTime() - a.lastSeenAt.getTime())
      .slice(0, MAX_DEVICES);

    tx.update(ref, {
      fcmDevices: devices,
      fcmTokens: devices.map((device) => device.token),
    });
  });

  return userDocFound;
}

function bindForeground(messaging: Messaging, registration: ServiceWorkerRegistration): void {
  if (foregroundBound) return;
  onMessage(messaging, (payload) => {
    const title = payload.notification?.title || 'Consiliere360';
    const body = payload.notification?.body || '';
    const url = payload.data?.url || '/';
    if (Notification.permission === 'granted') {
      void registration.showNotification(title, {
        body,
        icon: '/icons/icon-192.png',
        data: { url },
      });
    }
  });
  // Latched only once the listener is attached, so a throwing `onMessage` can be retried.
  foregroundBound = true;
}

async function currentRegistration(): Promise<ServiceWorkerRegistration> {
  // Dedicated scope so the messaging worker does not take control away from /sw.js
  return navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/firebase-cloud-messaging-push-scope',
  });
}

export async function syncFcmToken(userId: string): Promise<PushSyncStatus> {
  if (!userId) return report('no-user');
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return report('no-notification-api');
  }
  if (!('serviceWorker' in navigator)) return report('no-service-worker');
  if (!VAPID_KEY) return report('missing-vapid-key');
  if (Notification.permission !== 'granted') {
    return report('permission-not-granted', Notification.permission);
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) return report('unsupported-browser');

  try {
    const registration = await currentRegistration();
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return report('no-token-issued');

    if (!(await upsertDevice(userId, token))) return report('user-doc-missing');

    bindForeground(messaging, registration);
    return report('registered');
  } catch (error) {
    // Carries the FCM/Firestore error verbatim -- token-subscribe-failed, permission-blocked
    // on the push service, or a rules rejection on the write all land here.
    return report('subscribe-failed', error);
  }
}

/** Remove this device token before sign-out so leftover pushes do not follow a shared browser. */
export async function removeFcmToken(userId: string): Promise<void> {
  if (!userId || !canUseWebPush()) return;
  if (Notification.permission !== 'granted') return;

  const supported = await isSupported().catch(() => false);
  if (!supported) return;

  try {
    const registration = await currentRegistration();
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      const ref = doc(db, 'users', userId);
      // Same reason as `upsertDevice`: the array is rewritten wholesale, so the read has to be
      // part of the write or a concurrent sync elsewhere loses its registration.
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return;

        const remaining = readDevices(snap.data(), new Date()).filter(
          (device) => device.token !== token
        );
        tx.update(ref, {
          fcmDevices: remaining,
          fcmTokens: remaining.map((device) => device.token),
        });
      });
    }

    await deleteToken(messaging).catch(() => undefined);
  } catch (error) {
    console.warn('FCM token remove failed:', error);
  }
}
